import React, { ReactNode, useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { FullPageLoader } from '@tassomutuo-backoffice/ui';

import {
  getManagedLeadDocumentArchive,
  ManagedLeadDetail,
  ManagedLeadProductPath,
} from '../../../services/api/managed-leads/managedLeads';
import { classifyTassoQuintoError } from '../../../services/api/tasso-quinto';
import { downloadBlob } from '../../../utils/fileDownload';
import { formatManagedLeadStageAlert } from '../managedLeadFormatting';
import useManagedLeadNotes from '../hooks/useManagedLeadNotes';
import {
  MANAGED_LEAD_QUERY_OPTIONS,
  managedLeadDetailQueryKey,
} from '../managedLeadQueryKeys';
import {
  canCorrectManagedLeadRequestValues,
  MANAGED_LEAD_REQUEST_VALUES_EDIT_LABEL,
} from '../managedLeadRequestValues';
import { safeManagedLeadReturnTo } from '../managedLeadSecurity';
import ManagedLeadAppointmentBox from './ManagedLeadAppointmentBox';
import ManagedLeadNotesModal from './ManagedLeadNotesModal';
import ManagedLeadRequestValuesModal from './ManagedLeadRequestValuesModal';
import ManagedLeadSectionEditButton from './ManagedLeadSectionEditButton';
import ManagedLeadSummaryBox from './ManagedLeadSummaryBox';
import ManagedLeadUpdateModal from './ManagedLeadUpdateModal';
import {
  ManagedLeadUpdateRequestButton,
  useCanRequestManagedLeadUpdate,
} from './ManagedLeadUpdateRequestButton';
import TassoQuintoErrorAlert from './TassoQuintoErrorAlert';

interface ManagedProductLeadDetailPageProps<TDetail extends ManagedLeadDetail> {
  product: ManagedLeadProductPath;
  /** Product label shown above the lead name, e.g. "Prestiti". */
  productName: string;
  loadingMessage: string;
  getLead: (dealId: string) => Promise<TDetail>;
  /**
   * Product-native sections rendered under the shared summary. The correction
   * pencil is handed over ready to drop into the header of the values section,
   * or `null` where the stage does not allow a correction: the scaffold owns
   * the modal behind it, so a product page only decides where it sits.
   */
  renderProductSections: (
    detail: TDetail,
    requestValuesAction: ReactNode
  ) => ReactNode;
}

/**
 * Read-only detail scaffold shared by every managed lead product: it owns the
 * detail query, the loading/error states, the common summary, the Notes modal
 * and the validated return to the product list, so a product page only has to
 * describe its own fields.
 */
const ManagedProductLeadDetailPage = <TDetail extends ManagedLeadDetail>({
  product,
  productName,
  loadingMessage,
  getLead,
  renderProductSections,
}: ManagedProductLeadDetailPageProps<TDetail>) => {
  const { dealId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const [showNotes, setShowNotes] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [showRequestValues, setShowRequestValues] = useState(false);
  const notesState = useManagedLeadNotes(product, dealId);
  const canRequestUpdate = useCanRequestManagedLeadUpdate();
  const returnTo = safeManagedLeadReturnTo(
    product,
    searchParams.get('returnTo'),
    dealId
  );
  const detailQuery = useQuery({
    ...MANAGED_LEAD_QUERY_OPTIONS,
    queryKey: managedLeadDetailQueryKey(product, dealId),
    queryFn: () => getLead(dealId),
    enabled: dealId.length > 0,
  });
  const detail = detailQuery.data;
  const documentArchiveMutation = useMutation({
    mutationFn: () => getManagedLeadDocumentArchive(product, dealId),
    onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
    onError: (error) => {
      const errorKind = classifyTassoQuintoError(error);
      if (errorKind === 'not_found') {
        toast.info('Nessun documento disponibile per questa lead.');
        return;
      }
      if (errorKind === 'unauthorized') {
        toast.error('Non sei autorizzato a scaricare questi documenti.');
        return;
      }
      if (errorKind === 'session_expired') {
        toast.error('La sessione è scaduta. Accedi di nuovo.');
        return;
      }
      toast.error('Non è stato possibile scaricare i documenti. Riprova.');
    },
  });

  if (detailQuery.isPending) {
    return (
      <>
        <FullPageLoader show />
        <Typography role="status" sx={{ position: 'absolute', left: -10000 }}>
          {loadingMessage}
        </Typography>
      </>
    );
  }

  if (detailQuery.isError || !detail) {
    return (
      <Box px={{ xs: 2, md: 4 }} py={4}>
        <TassoQuintoErrorAlert
          error={detailQuery.error}
          retry={() => detailQuery.refetch()}
          retryableMessage="Non è stato possibile caricare il dettaglio della lead."
          unauthorizedMessage="Non sei autorizzato a vedere questa lead."
          notFoundMessage="La lead richiesta non è disponibile."
        />
      </Box>
    );
  }

  return (
    <Box px={{ xs: 2, md: 4 }} py={4} maxWidth={1120} mx="auto">
      <Box px={4} pb={2}>
        <Box
          component={RouterLink}
          to={returnTo}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: 'fit-content',
            color: 'inherit',
            textDecoration: 'none',
          }}
          className="no-print"
        >
          <ArrowBackIcon />
          <Typography fontWeight="bold" color="primary">
            Indietro
          </Typography>
        </Box>
      </Box>

      <Typography
        display="block"
        variant="overline"
        color="text.secondary"
        fontWeight="bold"
        mb={1}
      >
        {`${productName} · Dettaglio lead`}
      </Typography>

      {detail.stage.alert && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {formatManagedLeadStageAlert(detail.stage.alert)}
        </Alert>
      )}

      <Stack spacing={3}>
        <ManagedLeadSummaryBox
          detail={detail}
          isDownloadingDocuments={documentArchiveMutation.isPending}
          onDownloadDocumentsPress={() => documentArchiveMutation.mutate()}
          onShowNotesPress={() => setShowNotes(true)}
          onUpdatePress={() => setShowUpdate(true)}
          updateRequestAction={
            // In gestione only, as on the list: a won or dropped lead has
            // nothing left for its owner to update.
            canRequestUpdate && detail.stage.category === 'in_progress' ? (
              <ManagedLeadUpdateRequestButton
                product={product}
                dealId={dealId}
              />
            ) : null
          }
        />

        <Box
          display="flex"
          flexWrap="wrap-reverse"
          alignItems="flex-end"
          columnGap={4}
        >
          {renderProductSections(
            detail,
            canCorrectManagedLeadRequestValues(detail) ? (
              <ManagedLeadSectionEditButton
                label={MANAGED_LEAD_REQUEST_VALUES_EDIT_LABEL}
                onPress={() => setShowRequestValues(true)}
              />
            ) : null
          )}
          <ManagedLeadAppointmentBox
            detail={detail}
            onEditPress={() => setShowUpdate(true)}
          />
        </Box>
      </Stack>

      <ManagedLeadNotesModal
        open={showNotes}
        handleClose={() => setShowNotes(false)}
        notesState={notesState}
      />
      {/* Keyed by lead: a draft Nota must never follow the operator onto
          another lead when the route changes under the same component. */}
      <ManagedLeadUpdateModal
        key={dealId}
        open={showUpdate}
        handleClose={() => setShowUpdate(false)}
        product={product}
        dealId={dealId}
        detail={detail}
      />
      {/* Kept mounted, and keyed by lead: a correction whose automatic Nota
          failed is outstanding until it is retried, and closing the modal must
          not throw away the only request that can still complete it. The form
          itself restarts from the backend at every opening. */}
      <ManagedLeadRequestValuesModal
        key={`request-values-${dealId}`}
        open={showRequestValues}
        handleClose={() => setShowRequestValues(false)}
        product={product}
        dealId={dealId}
        detail={detail}
      />
    </Box>
  );
};

export default ManagedProductLeadDetailPage;
